<template>
  <div
    :class="{ 'empty-state-card': true, 'empty-state-card--no-image': !showImage }"
    data-pendo-ds="empty_state_card"
    :style="{ width }"
  >
    <div class="empty-state-card__gutter" />
    <div class="empty-state-card__description">
      <div
        v-if="title || $slots.title"
        class="empty-state-card__text-content"
      >
        <slot name="title">
          <div class="body-l-medium">
            {{ title }}
          </div>
        </slot>
      </div>
      <div
        v-if="body || $slots.body"
        class="empty-state-card__text-content"
      >
        <slot name="body">
          <div class="body-m-regular">
            {{ body }}
          </div>
        </slot>
      </div>
      <div
        v-if="hasButton"
        class="empty-state-card__button"
      >
        <slot name="button">
          <BaseButton
            v-bind="button"
            emphasis="regular"
            @click="$emit('click')"
          />
        </slot>
      </div>
    </div>
    <div
      v-if="showImage"
      class="empty-state-card__gutter"
    />
    <div
      v-if="showImage"
      class="empty-state-card__image"
      :class="{ 'empty-state-card__image--hideable': !showMobileImage }"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        alt="parachute empty state"
        height="217"
        width="217"
        v-html="parachute"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <div class="empty-state-card__gutter" />
  </div>
</template>

<script>
import { isEmpty } from 'lodash'

import BaseButton from './BaseButton.vue'
import parachute from '../assets/images/empty_states/parachute.svg?raw'


export default {
  name: 'EmptyStateCard',
  components: {
    BaseButton,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    body: {
      type: String,
      default: '',
    },
    button: {
      type: Object,
      default: () => ({}),
      validator: (value) => {
        return 'icon' in value || 'text' in value || isEmpty(value)
      },
    },
    showImage: {
      type: Boolean,
      default: true,
    },
    width: {
      type: String,
      default: 'auto',
    },
    showMobileImage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    hasButton() {
      return ((this.button?.icon || this.button?.text)) || this.$slots.button
    },
    parachute() {
      return parachute
    },
  },
}

</script>
<style lang="scss">
  .empty-state-card__image:hover {
    #parachute, #torso, #head, #right_arm, #left_arm, #legs, #basket_checkmarks {
      animation: parachute-dude-2 4s ease-in 1, parachute-dude 4s ease-in-out infinite alternate;
    }
    #cloud1, #cloud2, #cloud {
      fill: #F2F2F2;
      fill-opacity: 0;
    }
    #cloud {
      animation: cloud-movement 10s linear infinite;
    }
    #cloud1 {
      animation: cloud-movement 15s linear 2s infinite;
    }
    #cloud2 {
      animation: cloud-movement 13s linear 4s infinite;
    }
  }

  @keyframes cloud-movement {
    0% {
      transform: translate(130px);
      fill-opacity: 0.8;
    }
    70% {
      transform: translate(-180px) scale(1.2);
      fill-opacity: 0.8;
    }
    75% {
      transform: translate(-180px) scale(1.2);
      fill-opacity: 0;
    }
    76% {
      transform: translate(130px) scale(1.2);
      fill-opacity: 0;
    }
    80% {
      transform: translate(130px) scale(1);
      fill-opacity: 0;
    }
    100% {
      transform: translate(130px);
      fill-opacity: 0.8;
    }
  }

  @keyframes parachute-dude-2 {
    0% {
      fill: #3389F4;
      transform: translateY(-10px) scale(.9);
    }
    25% {
      transform: translateY(0px) scale(1) rotate(4deg);
    }
    50% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(-4deg)
    }
  }

  @keyframes parachute-dude {
    0% {
      fill: #3389F4;
    }
    25% {
      transform: rotate(4deg);
    }
    50% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(-4deg)
    }
  }
</style>

<style scoped lang="scss">
  .empty-state-card {
    display: grid;
    grid-template-columns: 2fr auto 1fr auto 2fr;
    border: 1px solid var(--pepper-200);
    border-radius: var(--cr-br-small);
    background:var(--baja-blast-50);
    max-width: 100%;
    overflow-x: hidden;
  }

  .empty-state-card--no-image {
    grid-template-columns: auto 1fr auto;
  }

  .empty-state-card__content {
    display: flex;
    align-items: center;
  }

  .empty-state-card__text-content {
    min-width: 125px;
  }

  .empty-state-card__description {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-5) var(--space-7);
  }

  .empty-state-card__button {
    display: inline-block;
    padding-top: var(--space-3);
  }

  :deep(circle) {
    fill: var(--baja-blast-300)
  }

  :deep(#parachute, #torso, #head, #right_arm, #left_arm, #legs, #basket_checkmarks) {
    fill: #3389F4;
    transform-origin: 50px 50px;
  }

  :deep(#cloud1, #cloud2, #cloud) {
    fill: #F2F2F2;
  }

  @media (max-width: 500px) {
    .empty-state-card__image--hideable {
      display: none;
    }
  }
</style>
